import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter, ApiPage } from "@result/rui/utils/api";

import apiClient from "#/redux/api/ApiClient";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils.js";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/izlocitev/izlocitev";
export const createElimination = createAsyncThunk(
  "elimination/create",
  async (dto, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`${baseUrl}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * PUT /api/dog/izlocitev/izlocitev/{id}
 * */
export const updateElimination = createAsyncThunk(
  "elimination/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`${baseUrl}/${id}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteElimination = createAsyncThunk(
  "elimination/delete",
  async ({ id, version }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(
        `${baseUrl}/${id}?version=${encodeURIComponent(version)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * APi to check uniqueness of elimination record based on animalId and datumIzlocitve
 */
export const checkEliminationUnique = createAsyncThunk(
  "elimination/checkUnique",
  async ({ animalId, datumIzlocitve }, { rejectWithValue }) => {
    try {
      // implement API using calling page and then if any results return, then it returns false.
      const response = await apiClient.get(
        `${baseUrl}/page`,
        null,
        new ApiPage().page(0),
        new ApiFilter().group((group) => {
          group.equal("zival", animalId);
          group.equal("datumIzlocitve", datumIzlocitve);
        }),
        null
      );
      return response.data.totalElements === 0;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const addAnimalsToEliminationBulk = createAsyncThunk(
  "/elimination/addAnimalsToEliminationBulk",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        payload.map((animal) => dispatch(createElimination(animal)).unwrap())
      );

      const succeeded = [];
      const failed = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          succeeded.push(result.value);
        } else {
          failed.push({
            animal: payload[index],
            error: getErrorMessage(result.reason),
          });
        }
      });

      return { succeeded, failed, total: payload.length };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 *  GET /api/dog/izlocitev/page
 *
 */
export const fetchEliminationPage = createAsyncThunk(
  "elimination/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/page`,
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/dog/izlocitev/izlocitev/{id}
 */
export const fetchEliminationById = createAsyncThunk(
  "elimination/byId",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const allThunks = [
  fetchEliminationPage,
  fetchEliminationById,
  createElimination,
  updateElimination,
  addAnimalsToEliminationBulk,
];

const eliminationSlice = createSlice({
  name: "elimination",
  initialState: {
    page: {},
    error: null,
    isLoading: false,
    create: { isLoading: false, error: null, last: null },
  },
  reducers: {
    resetElimination: (state) => {
      state.create = { isLoading: false, error: null, last: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createElimination.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(addAnimalsToEliminationBulk.fulfilled, (state, action) => {
        state.create.isLoading = false;
        state.create.error = null;
        state.create.last = action.payload;
      });

    /* Get Page Eliminations */
    builder.addCase(fetchEliminationPage.fulfilled, (state, action) => {
      state.page = action.payload;
    });

    /* Get Elimination by Id */
    builder.addCase(fetchEliminationById.fulfilled, (state, action) => {
      state.byId = state.byId || {};
      const elimination = action.payload;
      state.byId[elimination.id] = elimination;
    });

    /* Update Elimination */
    builder.addCase(updateElimination.fulfilled, (state, action) => {
      state.byId = state.byId || {};
      const elimination = action.payload;
      state.byId[elimination.id] = elimination;
    });

    /* Delete Elimination */
    builder.addCase(deleteElimination.fulfilled, (state, action) => {
      state.byId = state.byId || {};
      const deletedElimination = action.payload;
      const deletedId = deletedElimination?.output;

      if (deletedId) {
        delete state.byId[deletedId];
        if (state.page?.content) {
          state.page.content = state.page.content.filter(
            (item) => String(item.id) !== String(deletedId)
          );
        }
      }
    });

    handleLoadingAndErrorState(builder, allThunks);
  },
});

export const { resetElimination } = eliminationSlice.actions;
export default eliminationSlice.reducer;
